import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Color from "@arcgis/core/Color";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import { getEmassarData } from "../services/api";
import {
  setEstate,
  setOpenedFromMapCitiesCard,
} from "../services/reducers/cities";
import { handleSelectCard } from "./cardNavigation";
import { LayersDataTypes, MainDataTypeIds } from "../types/layers-data-types";
import { EmassarMethod } from "../types/emassar-method";
import html2canvas from "html2canvas";
import { changeCoordinates, setLayersVisible } from "../services/reducers/map";
import { setCurrentOpenedCardPage } from "../services/reducers/filters";
import positionIcon from "../images/position-blue.svg";
import MapView from "@arcgis/core/views/MapView";
import ArcgisMap from "@arcgis/core/Map";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import { ArcgisHybridLayer, ArcgisTerrainLayer } from "../interfaces/layer";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { setZoom } from "../services/reducers/map";
import { getFid } from "./plot-utils";
import Legend from "@arcgis/core/widgets/Legend";
import Expand from "@arcgis/core/widgets/Expand";
import { LayerType } from "../types/layer-type";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Collection from "@arcgis/core/core/Collection";
import { handleFeatureClickStyle } from "./master-map-helper";

let polyline,
  polylineGraphic,
  tempLineGraphic,
  pointerMoveHandler,
  clickHandler,
  doubleClickHandler;
let lastPoint = null;
let totalDistance = 0;
let dynamicTextGraphic = null;
let segmentTextGraphics = [];
let lastSegmentTextGraphic = null;
let measuringLayer = null;
let originalRendererByLayer = [];
export let selectedFeature = null;
let lastSelectedFeature = null;
const savedRenderers = new WeakMap();
let timer;
let featureFocused = false;
let focusOnFeatureCallback = null;

export default function createViewer(store, addLayers, containerId, baseMap) {
  originalRendererByLayer = [];
  const { dispatch } = store;
  const { coordinates, zoom } = store.getState().map;
  const viewer = new MapView({
    map: new ArcgisMap({
      basemap: baseMap,
    }),
    container: containerId,
    ui: {
      components: [],
    },
    highlightOptions: {
      fillOpacity: 0,
      haloOpacity: 0,
    },
    popup: {
      defaultPopupTemplateEnabled: false,
      dockOptions: {
        buttonEnabled: false,
        breakpoint: false,
      },
      visibleElements: {
        collapseButton: false,
        heading: false,
        closeButton: false,
        featureNavigation: false,
        actionBar: false,
      },
    },
    center: [coordinates.longitude, coordinates.latitude],
    zoom: zoom,
  });

  viewer.addHandles(
    reactiveUtils.watch(
      () => viewer.scale,
      (scale) => {
        if (Number.isInteger(viewer.zoom)) {
          dispatch(setZoom({ scale, zoom: viewer.zoom }));
        }
      },
    ),
  );

  viewer.addHandles(
    reactiveUtils.watch(
      () => viewer.center,
      (center) => {
        setCoordinate(center, dispatch);
      },
    ),
  );
  viewer.ui.add(
    new ScaleBar({
      view: viewer,
    }),
    "bottom-left",
  );

  viewer.on("click", function (event) {
    unFocus();
    resetObjectStyle();
  });
  addLayers(viewer);
  return viewer;
}
export function CreateHybridBaseMap() {
  return new Basemap({
    baseLayers: [
      new TileLayer({
        url: "//services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
        id: "hybrid-base-layer",
      }),
      new VectorTileLayer({
        url: "https://cdn.arcgis.com/sharing/rest/content/items/2cab1abec2b2480bbda4cf034b3880f6/resources/styles/root.json",
        id: "hybrid-reference-layer",
      }),
    ],
    id: ArcgisHybridLayer.name,
  });
}
export function CreateTerrainBaseMap() {
  return new Basemap({
    baseLayers: [
      new TileLayer({
        url: "//services.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer",
        id: "world-hillshade-layer",
      }),
      new VectorTileLayer({
        url: "https://arcgis.com/sharing/rest/content/items/b5f2984babb34a99b698af685ae0779b/resources/styles/root.json",
      }),
      new VectorTileLayer({
        url: "https://arcgis.com/sharing/rest/content/items/d79de7efaba642fb9e8626fdbac86621/resources/styles/root.json",
      }),
    ],
    id: ArcgisTerrainLayer.name,
  });
}

export let excludedObjectIds = new Set();
export const cimLineSymbol = {
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          type: "CIMSolidStroke",
          enable: true,
          width: 2,
          color: [229, 158, 109, 255],
        },
        {
          type: "CIMSolidStroke",
          enable: true,
          width: 5,
          color: [255, 255, 255, 255],
        },
      ],
    },
  },
};

export const lineWithCircleSymbol = {
  type: "cim",
  data: {
    type: "CIMSymbolReference",
    symbol: {
      type: "CIMLineSymbol",
      symbolLayers: [
        {
          type: "CIMVectorMarker",
          enable: true,
          markerPlacement: {
            type: "CIMMarkerPlacementAtExtremities",
            angleToLine: true,
            extremityPlacement: "JustEnd",
          },
          frame: {
            xmin: 0,
            ymin: 0,
            xmax: 13,
            ymax: 13,
          },
          markerGraphics: [
            {
              type: "CIMMarkerGraphic",
              geometry: {
                rings: [
                  [
                    [6.5, 0],
                    [11.5, 2],
                    [13, 6.5],
                    [11.5, 11],
                    [6.5, 13],
                    [1.5, 11],
                    [0, 6.5],
                    [1.5, 2],
                    [6.5, 0],
                  ],
                ],
              },
              symbol: {
                type: "CIMPolygonSymbol",
                symbolLayers: [
                  {
                    type: "CIMSolidFill",
                    enable: true,
                    color: [229, 158, 109, 190],
                    width: 7,
                  },
                  {
                    type: "CIMSolidStroke",
                    enable: true,
                    color: [255, 255, 255, 190],
                    width: 7,
                  },
                ],
              },
            },
          ],
        },
        {
          type: "CIMSolidStroke",
          enable: true,
          width: 2,
          color: [229, 158, 109, 190],
        },
        {
          type: "CIMSolidStroke",
          enable: true,
          width: 5,
          color: [255, 255, 255, 190],
        },
      ],
    },
  },
};

const polylineSymbol = cimLineSymbol;
const temporaryLineSymbol = lineWithCircleSymbol;

export function addMarker(point, viewer, graphicsLayer) {
  if (!point) return;

  const markerSymbol = new SimpleMarkerSymbol({
    color: new Color("#E59E6D"),
    size: 13,
    outline: {
      color: new Color("white"),
      width: 4,
    },
  });

  const markerGraphic = new Graphic({
    geometry: new Point({
      x: point[0],
      y: point[1],
      spatialReference: viewer.spatialReference,
    }),
    symbol: markerSymbol,
  });

  graphicsLayer.add(markerGraphic);
}

export function addTextSymbol(point, text, viewer, graphicsLayer) {
  if (!point) return;

  const textSymbol = new TextSymbol({
    text: text,
    color: new Color("#362044"),
    xoffset: 0,
    yoffset: -20,
    font: {
      size: 12,
      weight: "bold",
      family: "Cairo",
    },
  });

  const pointGraphic = new Graphic({
    geometry: new Point({
      x: point[0],
      y: point[1],
      spatialReference: viewer.spatialReference,
    }),
    symbol: textSymbol,
  });

  graphicsLayer.add(pointGraphic);

  return pointGraphic;
}

export function updateTextPosition(lastPoint, pointerPoint) {
  const angle =
    (Math.atan2(
      pointerPoint[1] - lastPoint[1],
      pointerPoint[0] - lastPoint[0],
    ) *
      180) /
    Math.PI;
  let xoffset = 0;
  let yoffset = -15;

  if (angle > -45 && angle < 45) {
    yoffset = -15;
  } else if (angle > 45 && angle < 135) {
    xoffset = 30;
    yoffset = 10;
  } else if (angle > -135 && angle < -45) {
    xoffset = -30;
    yoffset = 10;
  } else {
    yoffset = 15;
  }

  return { xoffset, yoffset };
}

export function startDrawingProcess(viewer) {
  if (!measuringLayer) {
    measuringLayer = new GraphicsLayer();
    viewer.map.add(measuringLayer);
  }

  polyline = new Polyline({
    paths: [[]],
    spatialReference: viewer.spatialReference,
  });

  polylineGraphic = new Graphic({
    geometry: polyline,
    symbol: polylineSymbol,
  });

  tempLineGraphic = new Graphic({
    symbol: temporaryLineSymbol,
  });

  measuringLayer.addMany([polylineGraphic, tempLineGraphic]);

  clickHandler = viewer.on("click", (event) =>
    addPoint(event, viewer, measuringLayer),
  );
  pointerMoveHandler = viewer.on("pointer-move", (event) =>
    updateTemporaryLine(event, viewer, measuringLayer),
  );
  doubleClickHandler = viewer.on("double-click", function (event) {
    event.stopPropagation();

    addPoint(event, viewer, measuringLayer);

    finishDrawingProcess(viewer, measuringLayer);
    setTimeout(() => {
      startDrawingProcess(viewer, measuringLayer);
    }, 0);
  });
}

function addPoint(event, viewer, graphicsLayer) {
  const point = viewer.toMap({ x: event.x, y: event.y });
  if (point) {
    addMarker([point.x, point.y], viewer, graphicsLayer);

    if (lastPoint) {
      const segment = {
        type: "polyline",
        paths: [[lastPoint, [point.x, point.y]]],
        spatialReference: viewer.spatialReference,
      };
      const segmentLength = geometryEngine.geodesicLength(segment, "meters");
      totalDistance += segmentLength;

      if (segmentLength === 0) {
        return;
      }

      const textGraphic = addTextSymbol(
        [point.x, point.y],
        `${segmentLength.toFixed(0)} m`,
        viewer,
        graphicsLayer,
      );
      segmentTextGraphics.push(textGraphic);

      lastSegmentTextGraphic = textGraphic;
    } else {
      const textGraphic = addTextSymbol(
        [point.x, point.y],
        "Start",
        viewer,
        graphicsLayer,
      );
      segmentTextGraphics.push(textGraphic);
    }

    polyline.paths[0].push([point.x, point.y]);
    polylineGraphic.geometry = polyline.clone();

    lastPoint = [point.x, point.y];

    tempLineGraphic.geometry = null;

    if (dynamicTextGraphic) {
      graphicsLayer.remove(dynamicTextGraphic);
      dynamicTextGraphic = null;
    }
  }
}

function updateTemporaryLine(event, viewer, graphicsLayer) {
  const point = viewer.toMap({ x: event.x, y: event.y });
  if (point && lastPoint) {
    const tempLine = new Polyline({
      paths: [[lastPoint, [point.x, point.y]]],
      spatialReference: viewer.spatialReference,
    });
    tempLineGraphic.geometry = tempLine;

    const segment = {
      type: "polyline",
      paths: [[lastPoint, [point.x, point.y]]],
      spatialReference: viewer.spatialReference,
    };
    const dynamicLength = geometryEngine.geodesicLength(segment, "meters");

    updateDynamicLength(
      [lastPoint[0], point.x],
      [lastPoint[1], point.y],
      dynamicLength,
      viewer,
      graphicsLayer,
    );
  } else {
    tempLineGraphic.geometry = null;

    if (dynamicTextGraphic) {
      graphicsLayer.remove(dynamicTextGraphic);
      dynamicTextGraphic = null;
    }
  }
}

function updateDynamicLength(xCoords, yCoords, length, viewer, graphicsLayer) {
  if (dynamicTextGraphic) {
    graphicsLayer.remove(dynamicTextGraphic);
  }

  const midPoint = [
    (xCoords[0] + xCoords[1]) / 2,
    (yCoords[0] + yCoords[1]) / 2,
  ];

  const offsets = updateTextPosition(
    [xCoords[0], yCoords[0]],
    [xCoords[1], yCoords[1]],
  );

  const dynamicTextSymbol = new TextSymbol({
    text: `${length.toFixed(0)} m`,
    color: "#362044",
    xoffset: offsets.xoffset,
    yoffset: offsets.yoffset,
    font: {
      size: 12,
      weight: "bold",
      family: "Cairo",
    },
    horizontalAlignment: "center",
    verticalAlignment: "middle",
  });

  const pointGraphic = new Graphic({
    geometry: {
      type: "point",
      x: midPoint[0],
      y: midPoint[1],
      spatialReference: viewer.spatialReference,
    },
    symbol: dynamicTextSymbol,
  });

  graphicsLayer.add(pointGraphic);
  dynamicTextGraphic = pointGraphic;
}

export function finishDrawingProcess(viewer) {
  if (tempLineGraphic) {
    measuringLayer.remove(tempLineGraphic);
    tempLineGraphic = null;
  }

  if (dynamicTextGraphic) {
    measuringLayer.remove(dynamicTextGraphic);
    dynamicTextGraphic = null;
  }

  if (lastSegmentTextGraphic) {
    measuringLayer.remove(lastSegmentTextGraphic);
    lastSegmentTextGraphic = null;
  }

  if (lastPoint && totalDistance > 0) {
    const totalTextGraphic = addTextSymbol(
      lastPoint,
      `${totalDistance.toFixed(0)} m`,
      viewer,
      measuringLayer,
    );
    segmentTextGraphics.push(totalTextGraphic);
  }

  if (pointerMoveHandler) {
    pointerMoveHandler.remove();
    pointerMoveHandler = null;
  }
  if (clickHandler) {
    clickHandler.remove();
    clickHandler = null;
  }
  if (doubleClickHandler) {
    doubleClickHandler.remove();
    doubleClickHandler = null;
  }

  lastPoint = null;
  totalDistance = 0;
  polyline = null;
  polylineGraphic = null;
  tempLineGraphic = null;
}

export function stopMeasuring(viewer) {
  if (measuringLayer) {
    viewer.map.remove(measuringLayer);
    measuringLayer = null;
  }
}

export const getPopUpTemplate = (labelConfig) => {
  const label = labelConfig?.expession
    ? `{${labelConfig.expession}}`
    : labelConfig?.text;

  return {
    content: [
      {
        type: "text",
        text: `
          <div style="
          background-color: #ffffff; 
          color: #362044; 
          font-size: 25px; 
          font-family: 'Cairo', sans-serif;
          font-weight: 700; 
          text-align: center;
          ">
            ${label}
          </div>
        `,
      },
    ],
    overwriteActions: true,
    lastEditInfoEnabled: false,
  };
};

export function applyHoverEffect(
  viewer,
  mapLayer,
  mode = "stylepopup",
  hoverStyle,
  fieldName = null,
) {
  let currentGraphicId = null;
  let popupVisible = false;

  viewer.whenLayerView(mapLayer).then(function () {
    const originalRenderer = mapLayer.renderer;
    originalRendererByLayer.push({
      mapLayer: mapLayer,
      renderer: originalRenderer,
    });
    viewer.on("pointer-move", function (event) {
      viewer.hitTest(event).then(function (response) {
        if (featureFocused) {
          return;
        }
        let clickedGraphicId =
          selectedFeature?.mapLayer === mapLayer
            ? selectedFeature.graphicId
            : null;
        const results = response.results.filter(
          (result) => result.graphic.layer === mapLayer,
        );
        if (results.length > 0) {
          const graphic = results[0].graphic;
          let graphicId =
            graphic.attributes[fieldName ?? mapLayer.objectIdField];
          const isEmpty = !fieldName || !graphicId;
          if (isEmpty) {
            graphicId = graphic.attributes[mapLayer.objectIdField];
          }

          if (excludedObjectIds.has(graphicId)) {
            return;
          }

          if (graphicId === clickedGraphicId) {
            return;
          }

          if (currentGraphicId !== graphicId) {
            currentGraphicId = graphicId;
            if (mode.includes("style")) {
              setRenderer(
                mapLayer,
                originalRenderer,
                getHoverStyle(hoverStyle),
                getExpression(
                  isEmpty
                    ? mapLayer.objectIdField
                    : (fieldName ?? mapLayer.objectIdField),
                  [currentGraphicId, clickedGraphicId],
                ),
              );
            }

            if (mode.includes("popup")) {
              let center = graphic.geometry.centroid;
              viewer
                .openPopup({
                  location: center,
                  features: [graphic],
                })
                .finally(() => (popupVisible = true));
            }
          }
        } else {
          if (currentGraphicId !== null) {
            currentGraphicId = null;

            if (popupVisible) {
              viewer.closePopup();
              popupVisible = false;
            }
            if (mode.includes("style")) {
              setRenderer(
                mapLayer,
                originalRenderer,
                getHoverStyle(hoverStyle),
                getExpression(fieldName ?? mapLayer.objectIdField, [
                  currentGraphicId,
                  clickedGraphicId,
                ]),
              );
            }
          }
        }
      });
    });
  });
}

export function resetHoverEffects(viewer) {
  if (originalRendererByLayer.length) {
    originalRendererByLayer.forEach((layerData) => {
      layerData.mapLayer.renderer = layerData.renderer;
    });
  }

  if (viewer.popup?.visible) {
    viewer.closePopup();
  }
}

function getHoverStyle(symbol) {
  return { value: "hover", symbol: symbol, label: "Selected" };
}

export function setRenderer(
  mapLayer,
  originalRenderer,
  style,
  expression,
  saveRenderer = false,
) {
  if (!originalRenderer) return;
  if (savedRenderers.has(mapLayer) && style) {
    originalRenderer = savedRenderers.get(mapLayer);
    mapLayer.renderer = originalRenderer;
  }

  if (!expression) {
    mapLayer.renderer = originalRenderer;
    return;
  }

  const uniqueValueInfos = [];
  if (style) {
    uniqueValueInfos.push(style);
  }

  if (originalRenderer.type === "unique-value") {
    originalRenderer.uniqueValueInfos.forEach((original) => {
      uniqueValueInfos.push({
        value: original.value,
        symbol: original.symbol,
        label: original.label,
      });
    });
    mapLayer.renderer = {
      type: "unique-value",
      valueExpression: getValueExpression(
        originalRenderer,
        expression,
        style?.value,
      ),
      uniqueValueInfos: uniqueValueInfos,
      defaultSymbol: originalRenderer.defaultSymbol,
      defaultLabel: originalRenderer.defaultLabel,
    };
  } else {
    mapLayer.renderer = {
      type: "unique-value",
      valueExpression: style?.value
        ? `When(${expression}, '${style.value}', 'other')`
        : `When(${expression}, 'other')`,
      uniqueValueInfos: uniqueValueInfos,
      defaultSymbol: originalRenderer.symbol,
    };
  }

  if (saveRenderer) {
    savedRenderers.set(mapLayer, mapLayer.renderer);
  }
}

export function getValueExpression(originalRenderer, expression, value = null) {
  if (originalRenderer.type === "unique-value") {
    let baseExpression = originalRenderer.valueExpression || "";

    if (baseExpression.startsWith("When(")) {
      baseExpression = baseExpression.slice(5);
    }
    if (baseExpression.endsWith(")")) {
      baseExpression = baseExpression.slice(0, -1);
    }

    return value
      ? `When(${expression}, '${value}', ${baseExpression || "'other'"})`
      : `When(${expression}, ${baseExpression || "'other'"})`;
  }

  return value
    ? `When(${expression}, '${value}', $feature.${originalRenderer.field})`
    : `When(${expression}, $feature.${originalRenderer.field})`;
}

export function getExpression(objectIdField, array) {
  return array
    .filter((item) => item)
    .map((item) => `$feature.${objectIdField} == ${item}`)
    .join(" || ");
}

export function getOriginalRenderer(mapLayer) {
  return originalRendererByLayer.find((item) => item.mapLayer === mapLayer)
    ?.renderer;
}
export function handleMapFeatureClick(event, viewer, store, dispatch) {
  viewer.hitTest(event).then((response) => {
    if (response.results.length === 0) return;
    const graphics = response.results
      .filter((res) => res.graphic)
      .filter((res) => res.graphic.layer?.id)
      .map((res) => res.graphic);
    if (graphics.length === 0) return;
    const layers = store
      .getState()
      .map.layers.filter((l) => MainDataTypeIds.includes(l.dataType));
    const result = graphics
      .map((graphic) => ({
        graphic: graphic,
        layer: layers.find((l) => l.name === graphic.layer.id),
      }))
      .find((r) => r.layer);
    if (!result) return;
    const { graphic, layer } = result;

    if (layer) {
      const fieldName = layer.style2D?.fieldName;
      const mapLayer = graphic.layer;
      let graphicId = graphic.attributes[fieldName ?? mapLayer.objectIdField];
      if (!graphicId) {
        graphicId = graphic.attributes[mapLayer.objectIdField];
      }

      if (
        lastSelectedFeature?.graphicId === graphicId &&
        lastSelectedFeature?.mapLayer === mapLayer
      ) {
        return;
      }

      lastSelectedFeature = { mapLayer, graphicId };

      const attributes = graphic.attributes;

      if (layer.dataType === LayersDataTypes.CityBoundary) {
        dispatch(setEstate(attributes.estateEn));
        dispatch(setOpenedFromMapCitiesCard(true));
        dispatch(setCurrentOpenedCardPage("cities"));
      } else if (layer.dataType === LayersDataTypes.InvestmentOpportunities) {
        handleSelectCard(
          dispatch,
          "InvestmentOpportunities",
          getFid(attributes).toString(),
          true,
        );
      } else if (layer.dataType === LayersDataTypes.SpacesForRent) {
        handleSelectCard(
          dispatch,
          "SpacesForRent",
          getFid(attributes).toString(),
          true,
        );
      } else if (layer.dataType === LayersDataTypes.Plots) {
        handleSelectCard(
          dispatch,
          "Plots",
          getFid(attributes).toString(),
          true,
        );
      }
    }
  });
}

export function highlightObject(objectId, layer, viewer, fieldName = null) {
  const mapLayer = viewer.map.layers.find((l) => l.id === layer.name);
  if (!mapLayer) return;

  const field = fieldName ?? mapLayer.objectIdField;

  const query = mapLayer.createQuery();
  query.where = `${field} = ${objectId}`;
  query.outFields = ["*"];
  query.returnGeometry = true;

  mapLayer.queryFeatures(query).then((result) => {
    if (result.features.length > 0) {
      selectedFeature = {
        mapLayer: mapLayer,
        graphicId: objectId,
      };
      setRenderer(
        mapLayer,
        getOriginalRenderer(mapLayer),
        getHoverStyle(layer.style2D?.hover),
        getExpression(field, [objectId]),
      );
    }
  });
}

export function resetObjectStyle() {
  if (!selectedFeature?.mapLayer) {
    return;
  }

  const mapLayer = selectedFeature.mapLayer;

  if (savedRenderers.has(mapLayer)) {
    mapLayer.renderer = savedRenderers.get(mapLayer);
  } else {
    mapLayer.renderer = getOriginalRenderer(mapLayer);
  }

  selectedFeature = null;
  lastSelectedFeature = null;
}

export function AddCityBoundaryLayer(view, layerProperties, layer, marker) {
  const featureLayer = new FeatureLayer(layerProperties);
  const markerLayer = new GraphicsLayer({
    id: layer.name,
    visible: featureLayer.visible,
  });
  view.map.add(featureLayer);
  view.map.add(markerLayer);
  const query = featureLayer.createQuery();
  featureLayer.queryFeatures(query).then(async function (results) {
    let estates = results.features.map((feature) => {
      return {
        id: feature.getObjectId(),
        estate: feature.attributes.estateEn,
        geometry: feature.geometry,
      };
    });
    const groups = Map.groupBy(estates, ({ estate }) => estate);
    let estateDatas = await getEmassarData(
      EmassarMethod.Estate,
      Array.from(groups.keys()).filter((estateId) => estateId != null),
    );
    groups.entries().forEach((group) => {
      let estateId = group[0];
      if (estateId) {
        const estateValues = featureLayer.sourceJSON.fields.find(
          (item) => item.name === "estateEn",
        ).domain.codedValues;
        const geom = geometryEngine.union(group[1].map((g) => g.geometry));
        const focusMarker = new Graphic({
          geometry: geom.extent.center,
          symbol: marker,
          attributes: {
            id: group[1][0].id,
            estate: estateValues.find((v) => v.code === estateId).name,
            rent_policy: estateDatas.find((e) => e.estateId === estateId)
              ?.rent_policy_en,
            estateId,
          },
          popupEnabled: false,
          popupTemplate: {
            content: [
              {
                type: "text",
                text: `
                    <div style="
                    background-color: #ffffff; 
                    color: #362044; 
                    font-size: 25px; 
                    font-family: 'Cairo', sans-serif;
                    font-weight: 700; 
                    text-align: center;
                    ">
                      ${"{estate}"}
                    </div>  
                    <div style="
                    background-color: #ffffff; 
                    color: #362044; 
                    font-size: 12px; 
                    font-family: 'Cairo', sans-serif;
                    font-weight: 400;
                    ">
                      ${"{rent_policy}"}
                    </div>                   
                  `,
              },
            ],
            overwriteActions: true,
          },
        });
        focusMarker.getObjectId();
        markerLayer.add(focusMarker);
      } else {
        group[1].forEach((item) => {
          const focusMarker = new Graphic({
            geometry: item.geometry,
            symbol: marker,
            attributes: {
              id: item.id,
              estate: "No information",
            },
            popupEnabled: false,
            popupTemplate: {
              content: [
                {
                  type: "text",
                  text: `
                    <div style="
                    background-color: #ffffff; 
                    color: #362044; 
                    font-size: 25px; 
                    font-family: 'Cairo', sans-serif;
                    font-weight: 700; 
                    text-align: center;
                    ">
                      ${"{estate}"}
                    </div>                   
                  `,
                },
              ],
              overwriteActions: true,
            },
          });
          markerLayer.add(focusMarker);
        });
      }
    });
  });
  let hoverStyle = layer.style2D?.hover;
  if (hoverStyle) {
    applyHoverEffect(
      view,
      featureLayer,
      "style",
      hoverStyle,
      layer.style2D?.fieldName,
    );
  }
  applyHoverEffect(view, markerLayer, "popup", null, "id");
  return featureLayer;
}

export function waitViewerUpdate(view) {
  return Promise.race([
    new Promise((resolve) => {
      const handle = view.watch("updating", (updating) => {
        if (!updating) {
          handle.remove();
          resolve();
        }
      });
    }),
    new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 5000);
    }),
  ]);
}

export async function createScreenshot(
  viewer,
  parameters,
  getBaseMap,
  delayBeforeScreenshot = false,
  forceLayerVisibility = false,
) {
  const layers = parameters.layers
    ?.map((layer) => viewer.map.layers.find((l) => l.id === layer.name))
    .filter((l) => l !== undefined);
  const graphicsLayers = parameters.graphicsLayers?.filter((l) => l) || [];
  await viewer.goTo({
    target: parameters.geometry,
    ...(parameters.scale ? { scale: parameters.scale } : {}),
  });
  if (typeof parameters.zoomAdjustment === "number") {
    await viewer.goTo({ zoom: viewer.zoom + parameters.zoomAdjustment });
  }
  const oldBaseMap = viewer.map.basemap;
  const baseMap = parameters.layers.find(
    (l) => l.dataType === LayersDataTypes.BaseMap,
  );
  if (baseMap) {
    changeBaseMap(baseMap, viewer, getBaseMap);
  }
  const originalVisibility = new Map();

  if (forceLayerVisibility) {
    layers.forEach((layer) => {
      originalVisibility.set(layer, layer.visible);
      if (!layer.visible) layer.visible = true;
    });
  }

  await waitViewerUpdate(viewer);

  if (delayBeforeScreenshot) {
    await new Promise((resolve) => setTimeout(resolve, 5000));
  }

  const screenshot = await viewer.takeScreenshot({
    width: parameters.width,
    height: parameters.height,
    layers: [...viewer.map.basemap.baseLayers, ...layers, ...graphicsLayers],
  });
  if (viewer.map.basemap.id !== oldBaseMap.id) viewer.map.basemap = oldBaseMap;

  originalVisibility.forEach((wasVisible, layer) => {
    layer.visible = wasVisible;
  });

  let dataUrl = screenshot.dataUrl;
  const currentScale = viewer.scale;
  const screenCanvas = document.createElement("canvas");
  screenCanvas.height = screenshot.data.height;
  screenCanvas.width = screenshot.data.width;
  const context = screenCanvas.getContext("2d");
  const img = document.createElement("img");
  img.src = screenshot.dataUrl;
  return new Promise((resolve) => {
    img.onload = function () {
      context.drawImage(img, 0, 0);
      if (
        parameters.text &&
        parameters.style &&
        parameters.label_x !== undefined &&
        parameters.label_y !== undefined
      ) {
        const labelCanvas = document.createElement("canvas");
        const label = document.createElement("div");
        label.textContent = parameters.text;
        label.style.cssText = parameters.style;
        document.body.append(label);
        html2canvas(label).then((labelResult) => {
          labelCanvas.height = labelResult.height;
          labelCanvas.width = labelResult.width;
          const labelContext = labelCanvas.getContext("2d");
          labelContext.beginPath();
          labelContext.roundRect(0, 0, labelCanvas.width, labelCanvas.height, [
            labelCanvas.height / 4,
          ]);
          labelContext.closePath();
          labelContext.clip();
          labelContext.drawImage(labelResult, 0, 0);
          context.drawImage(
            labelCanvas,
            parameters.label_x,
            parameters.label_y,
          );
          dataUrl = screenCanvas.toDataURL();
          document.body.removeChild(label);
          resolve(dataUrl);
        });
      } else {
        dataUrl = screenCanvas.toDataURL();
        resolve({ dataUrl, scale: Math.round(currentScale) });
      }
    };
  });
}

export async function createMultipleScreenshots(
  viewer,
  screenshotParamsArray,
  getBaseMap,
  delayBeforeScreenshot = false,
  forceLayerVisibility = false,
) {
  const screenshots = [];
  for (const params of screenshotParamsArray) {
    const screenshotDataUrl = await createScreenshot(
      viewer,
      params,
      getBaseMap,
      delayBeforeScreenshot,
      forceLayerVisibility,
    );
    screenshots.push(screenshotDataUrl);
  }
  return screenshots;
}

export function setCoordinate(center, dispatch) {
  clearTimeout(timer);
  timer = setTimeout(() => {
    dispatch(
      changeCoordinates({
        latitude: +center.latitude.toFixed(6),
        longitude: +center.longitude.toFixed(6),
      }),
    );
  }, 1000);
}

export function unFocus() {
  featureFocused = false;
  if (focusOnFeatureCallback) {
    focusOnFeatureCallback();
  }
  focusOnFeatureCallback = null;
}

export function handleFocusOnFeature(
  layer,
  geometry,
  objectId,
  dispatch,
  store,
  viewer,
  addMarker = true,
  changeStyle = true,
  zoomAdjustment,
) {
  if (focusOnFeatureCallback) {
    focusOnFeatureCallback();
  }

  if (layer) {
    dispatch(
      setLayersVisible([
        ...store.getState().map.visibleLayersId,
        layer.parentId ?? layer.id,
      ]),
    );
  }

  viewer.goTo({ target: geometry }).then(() => {
    if (typeof zoomAdjustment === "number") {
      viewer.goTo({
        zoom: viewer.zoom + zoomAdjustment,
      });
    }
  });

  const mapLayer = layer
    ? viewer.map.layers.find((l) => l.id === layer.name)
    : null;
  let originalRenderer = mapLayer ? getOriginalRenderer(mapLayer) : null;
  let markerLayer = null;
  const hoverStyle = layer?.style2D?.hover;

  if (addMarker) {
    markerLayer = new GraphicsLayer();
    viewer.map.add(markerLayer);
  }

  focusOnFeatureCallback = () => {
    if (savedRenderers.has(mapLayer)) {
      originalRenderer = savedRenderers.get(mapLayer);
      mapLayer.renderer = originalRenderer;
    } else if (originalRenderer) {
      mapLayer.renderer = originalRenderer;
    }
    viewer.map.remove(markerLayer);
  };
  if (hoverStyle && changeStyle) {
    setRenderer(
      mapLayer,
      originalRenderer,
      getHoverStyle(hoverStyle),
      `$feature.${mapLayer.objectIdField} == ${objectId}`,
    );
  }

  featureFocused = true;

  if (addMarker) {
    const focusMarker = new Graphic({
      geometry: geometry,
      symbol: {
        type: "picture-marker",
        url: positionIcon,
        width: "28px",
        height: "40px",
        yoffset: "16px",
      },
    });

    markerLayer?.graphics.add(focusMarker);
  }
}

export async function waitForViewerReady(viewer) {
  await viewer.when();
  await viewer.when(() => viewer.updating === false);
}

export function AddLegend(viewer, layer) {
  const legendExpand = viewer.ui.find("legendExpand");
  if (legendExpand) {
    legendExpand.content.layerInfos.push({
      layer: layer,
      title: layer.title,
    });
  } else {
    viewer.ui.empty("bottom-right");
    const legend = new Legend({
      view: viewer,
      respectLayerDefinitionExpression: true,
      hideLayersNotInCurrentView: true,
      headingLevel: 4,
      layerInfos: [
        {
          layer: layer,
          title: layer.title,
        },
      ],
    });
    const legendExpand = new Expand({
      expandIconClass: "esri-icon-drag-horizontal",
      expandTooltip: "Legend",
      view: viewer,
      content: legend,
      expanded: false,
      id: "legendExpand",
    });
    viewer.ui.add(legendExpand, "bottom-right");
  }
}
export function changeBaseMap(layer, viewer, getBaseMap) {
  if (viewer.map.basemap.id === layer.name) {
    return;
  }
  const baseMap = getBaseMap(layer);
  if (baseMap) {
    viewer.map.basemap = baseMap;
  } else {
    const baseLayer = getBaseLayer(layer);
    if (!baseLayer) return;
    const customBasemap = new Basemap({
      baseLayers: [baseLayer],
      id: layer.name,
      title: layer.label,
    });
    viewer.map.basemap = customBasemap;
  }
}
const getBaseLayer = (layer) => {
  let baseLayer;
  if (layer.type === LayerType.TMS) {
    baseLayer = new WebTileLayer({
      urlTemplate: layer.url.replace("{lang}", navigator.language),
      title: layer.label,
      id: layer.name,
    });
  } else if (layer.type === LayerType.ArcgisMapServer) {
    baseLayer = new MapImageLayer({
      title: layer.label,
      id: layer.name,
      url: layer.url,
    });
  }
  return baseLayer;
};
export const getGeometryType = (geometry) => {
  if (!geometry?.type) {
    if (geometry.paths) geometry.type = "polyline";
    else if (geometry.rings) geometry.type = "polygon";
    else if (geometry.x !== undefined && geometry.y !== undefined)
      geometry.type = "point";
  }
  return geometry;
};

export const getSymbolByGeometryType = (type) => {
  switch (type) {
    case "polygon":
      return new SimpleFillSymbol();
    case "polyline":
      return new SimpleLineSymbol();
    case "point":
      return new SimpleMarkerSymbol({
        style: "circle",
        color: [0, 0, 0, 1],
        size: 8,
      });
    default:
      return undefined;
  }
};

export const updateSnappingSources = (
  viewer,
  sketchViewModel,
  ignoredLayers = [],
) => {
  const sources = [...viewer.map.layers]
    .filter(
      (layer) =>
        !ignoredLayers.includes(layer) &&
        (layer instanceof GraphicsLayer || layer instanceof FeatureLayer),
    )
    .map((layer) => ({ layer }));

  sketchViewModel.snappingOptions.featureSources = new Collection(sources);
};

export const handleExtentEstate = (
  selectedCity,
  store,
  viewer,
  centerOnGraphic = true,
  centerOnExtent = true,
  highlightFeature = true,
) => {
  const mapLayers = store.getState().map.layers;

  if (centerOnGraphic) {
    const graphicsLayers = viewer.map.layers.items.filter(
      (layer) => layer.type === "graphics",
    );
    graphicsLayers.forEach((layer) => {
      const targetGraphic = layer.graphics.find(
        (graphic) => graphic.attributes?.estateId === selectedCity,
      );

      if (targetGraphic && targetGraphic.popupTemplate) {
        const center =
          targetGraphic.geometry.centroid ||
          targetGraphic.geometry.extent?.center;

        viewer.openPopup({
          location: center,
          features: [targetGraphic],
        });
      }
    });
  }

  if (centerOnExtent) {
    mapLayers.forEach((layer) => {
      if (layer.dataType === LayersDataTypes.CityBoundary) {
        const existingFeatureLayer = viewer.map.findLayerById(layer.name);

        existingFeatureLayer?.load().then(() => {
          existingFeatureLayer
            .queryFeatures({
              where: `estateEn = ${selectedCity}`,
              outFields: ["*"],
              returnGeometry: true,
            })
            .then((result) => {
              const features = result.features;
              if (centerOnExtent && highlightFeature) {
                handleFeatureClickStyle(features[0], store, [], viewer);
              }
              const extents = features.map(
                (feature) => feature.geometry.extent,
              );
              let combinedExtent = extents[0];
              for (let i = 1; i < extents.length; i++) {
                combinedExtent = combinedExtent.union(extents[i]);
              }
              if (combinedExtent) {
                viewer.goTo({
                  target: combinedExtent,
                });
              }
            });
        });
      }
    });
  }
};
